import { CommonModule } from '@angular/common';
import { Component, Inject } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { DateAdapter } from '@angular/material/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { DatePickerComponent } from '@shared/components/date-picker/date-picker.component';
import { ModalShellComponent, ModalShellConfig } from '@shared/components/modal-shell/modal-shell.component';
import { MembershipStatus } from '@core/api/generated/api-client';
import { membershipStatusOptions } from '@core/api/enums/membership-status.labels';

export interface PersonStatusDialogData {
  status?: MembershipStatus | null;
  targetStatus?: MembershipStatus;
}

export interface PersonStatusDialogResult {
  status: MembershipStatus;
  effectiveDate?: string | null;
  deathDate?: string | null;
}

@Component({
  selector: 'app-person-status-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatDialogModule,
    MatFormFieldModule,
    MatSelectModule,
    DatePickerComponent,
    ModalShellComponent,
  ],
  templateUrl: './person-status-dialog.component.html',
  styleUrl: './person-status-dialog.component.scss',
})
export class PersonStatusDialogComponent {
  readonly modalConfig: ModalShellConfig;
  readonly statusOptions: typeof membershipStatusOptions;
  readonly form: FormGroup;

  constructor(
    private readonly fb: FormBuilder,
    private readonly dateAdapter: DateAdapter<any>,
    private readonly dialogRef: MatDialogRef<PersonStatusDialogComponent, PersonStatusDialogResult | null>,
    @Inject(MAT_DIALOG_DATA) public readonly data: PersonStatusDialogData,
  ) {
    this.modalConfig = {
      title: data.targetStatus === 'Departed' ? 'Registrar fallecimiento' : 'Cambiar estado',
      size: 'sm',
      layout: 'form',
    };
    this.statusOptions = data.targetStatus
      ? membershipStatusOptions.filter(option => option.value === data.targetStatus)
      : membershipStatusOptions.filter(option => option.value !== data.status);

    this.form = this.fb.group({
      status: [data.targetStatus ?? null, Validators.required],
      deathDate: [null],
    });

    this.configureDeathDateValidator(this.form.get('status')!.value);
    this.form.get('status')!.valueChanges.subscribe(status => {
      this.configureDeathDateValidator(status);
    });
  }

  get requiresDeathDate(): boolean {
    return this.form.get('status')!.value === 'Departed';
  }

  onConfirm(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const status = this.form.get('status')!.value as MembershipStatus;
    const deathDate = this.form.get('deathDate')!.value;
    this.dialogRef.close({
      status,
      deathDate: status === 'Departed' ? this.toIsoDate(deathDate) : null,
    });
  }

  onCancel(): void {
    this.dialogRef.close(null);
  }

  private toIsoDate(value: any): string {
    const year = this.dateAdapter.getYear(value);
    const month = this.dateAdapter.getMonth(value) + 1;
    const day = this.dateAdapter.getDate(value);
    return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  }

  private configureDeathDateValidator(status: MembershipStatus): void {
    const deathDateControl = this.form.get('deathDate')!;
    if (status === 'Departed') {
      deathDateControl.setValidators([Validators.required]);
    } else {
      deathDateControl.clearValidators();
      deathDateControl.setValue(null);
    }
    deathDateControl.updateValueAndValidity();
  }
}
